import { ColumnDef } from "@tanstack/react-table"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import DatePicker from "react-datepicker"
import { useTranslation } from "react-i18next"

import { handleToast } from "utils/messages"
import { history } from "utils/routes"

import { getPlants } from "../../../companies/service/api"
import { Company } from "../../../companies/types"
import { MaterialGroup } from "../../../materials/service/api"
import { getProducts } from "../../../products/service/api"
import { Product } from "../../../products/types"
import {
  deleteSubmission,
  postConverterSubmissionOutput,
  putSubmission,
} from "../../service/api"
import { SubmissionType } from "../../types"
import SubmissionGrid, { GridRow } from "./SubmissionGrid"
import { useGridSubmit } from "./useGridSubmit"

type ConvertingOutputRow = {
  id?: number
  companyId?: number
  materialsGroupId?: number
  collectionDate?: Date
  productId?: number
  volumeNet?: number
  totalPercentageOfRecycledPlasticsInFrance?: number
  estimatedShareExportedInsideEu?: number
  estimatedShareExportedOutsideEu?: number
  administrationNumber?: string
}

type LocalRow = GridRow<ConvertingOutputRow>

const makeKey = () =>
  `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`

// Flatten the products tree into [{id, title}], filtered by category (= materialsGroupId).
const flattenProducts = (
  products: Product[],
  categoryId?: number,
): { id: number; title: string }[] => {
  const out: { id: number; title: string }[] = []
  const walk = (list: Product[], prefix = "") => {
    for (const p of list) {
      const title = prefix ? `${prefix} / ${p.title}` : p.title
      out.push({ id: p.id, title })
      if (p.children?.length) walk(p.children, title)
    }
  }
  const roots = categoryId != null
    ? products.filter((p) => p.category === categoryId)
    : products
  walk(roots)
  return out
}

const ConvertingOutputGrid: React.FC = () => {
  const { t } = useTranslation()

  const [plants, setPlants] = useState<Company[]>([])
  const [allProducts, setAllProducts] = useState<Product[]>([])

  const [rows, setRows] = useState<LocalRow[]>([{ key: makeKey(), row: {} }])

  const polymersOf = useCallback(
    (companyId?: number): MaterialGroup[] => {
      if (!companyId) return []
      const plant = plants.find((p) => p.id === companyId)
      return (plant?.polymers ?? []) as MaterialGroup[]
    },
    [plants],
  )

  // France-only tickbox removed per new spec.
  const buildPayload = useCallback(
    (row: ConvertingOutputRow) => {
      const volumeNet = row.volumeNet ?? 0
      return {
        companyId: row.companyId!,
        materialsGroupId: row.materialsGroupId!,
        collectionDate: row.collectionDate!,
        productId: row.productId ?? 0,
        isInput: false,
        type: SubmissionType.CONVERTING,
        volumeGross: volumeNet,
        volumeNet,
      totalPercentageOfRecycledPlasticsInFrance:
        row.totalPercentageOfRecycledPlasticsInFrance ?? 0,
      estimatedShareExportedInsideEu: row.estimatedShareExportedInsideEu ?? 0,
      estimatedShareExportedOutsideEu: row.estimatedShareExportedOutsideEu ?? 0,
      productsInFrance: false,
      administrationNumber: row.administrationNumber ?? "",
      }
    },
    [],
  )

  const { states, isSubmitting, submitAll, clearRowState } =
    useGridSubmit<ConvertingOutputRow>({
      validate: (row) => {
        if (
          row.companyId == null ||
          row.materialsGroupId == null ||
          row.collectionDate == null ||
          row.volumeNet == null
        ) {
          return null
        }
        const errors: string[] = []
        if (row.volumeNet <= 0) errors.push(t("Total must be > 0"))
        const france = row.totalPercentageOfRecycledPlasticsInFrance ?? 0
        const inside = row.estimatedShareExportedInsideEu ?? 0
        const outside = row.estimatedShareExportedOutsideEu ?? 0
        if (france < 0 || inside < 0 || outside < 0)
          errors.push(t("Estimated shares must be ≥ 0"))
        if (france + inside + outside > 100)
          errors.push(t("Estimated shares cannot exceed 100% combined"))
        return errors
      },
      create: async (row) => {
        const { data, errors } = await postConverterSubmissionOutput(
          buildPayload(row) as any,
        )
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data!.id! }
      },
      update: async (row) => {
        const { data, errors } = await putSubmission({
          ...buildPayload(row),
          id: row.id!,
        } as any)
        if (errors) return { errors: errors.map((e) => e.message) }
        return { id: data?.id ?? row.id! }
      },
    })

  useEffect(() => {
    getPlants().then((d) => {
      if (d.data) setPlants(d.data as Company[])
    })
    getProducts({}).then((d) => {
      if (d.data) setAllProducts(d.data as Product[])
    })
  }, [])

  const updateRow = useCallback(
    (key: string, patch: Partial<ConvertingOutputRow>) => {
      setRows((prev) =>
        prev.map((r) =>
          r.key === key ? { ...r, row: { ...r.row, ...patch } } : r,
        ),
      )
    },
    [],
  )

  const handleAdd = useCallback(() => {
    setRows((prev) => [...prev, { key: makeKey(), row: {} }])
  }, [])

  const handleRemove = useCallback(
    async (key: string) => {
      const target = rows.find((r) => r.key === key)
      clearRowState(key)
      if (target?.row.id != null) {
        const { errors } = await deleteSubmission({ id: target.row.id })
        if (errors) {
          handleToast(errors, "error")
          return
        }
      }
      setRows((prev) => prev.filter((r) => r.key !== key))
    },
    [clearRowState, rows],
  )

  const handleSubmit = useCallback(async () => {
    const { ok, savedCount, updated } = await submitAll(
      rows.map(({ key, row }) => ({ key, row })),
    )
    if (updated.length > 0) {
      setRows((prev) =>
        prev.map((r) => {
          const u = updated.find((x) => x.key === r.key)
          return u ? { ...r, row: { ...r.row, id: u.serverId } } : r
        }),
      )
    }
    if (ok && savedCount === 0) {
      handleToast(
        [t("Nothing to save — fill in the required fields first.") as string],
        "error",
      )
      return
    }
    if (ok) {
      handleToast([t("Submissions saved") as string])
      history.push("/submissions", { tab: "convertingOutput" })
    } else {
      handleToast(
        [
          t("Some rows could not be saved. Check the highlighted rows.") as string,
        ],
        "error",
      )
    }
  }, [rows, submitAll, t])

  const columns = useMemo<ColumnDef<LocalRow, any>[]>(
    () => [
      {
        id: "company",
        header: () => <span>{t("Plant name")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <select
              value={r.companyId ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  companyId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                  materialsGroupId: undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {plants.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 170,
      },
      {
        id: "polymer",
        header: () => <span>{t("Polymer")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const list = polymersOf(r.companyId)
          return (
            <select
              value={r.materialsGroupId ?? ""}
              disabled={!r.companyId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  materialsGroupId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                  productId: undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {list.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )
        },
        size: 140,
      },
      {
        id: "date",
        header: () => <span>{t("Date")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <DatePicker
              selected={r.collectionDate}
              dateFormat="MMM-yy"
              showMonthYearPicker
              showPopperArrow={false}
              onChange={(date: Date) =>
                updateRow(row.original.key, { collectionDate: date })
              }
              className="form-control"
              placeholderText={t("Pick month")}
            />
          )
        },
        size: 130,
      },
      {
        id: "product",
        header: () => <span>{t("Used In")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          const filteredProducts = flattenProducts(allProducts, r.materialsGroupId)
          return (
            <select
              value={r.productId ?? ""}
              disabled={!r.materialsGroupId}
              onChange={(e) =>
                updateRow(row.original.key, {
                  productId: e.target.value
                    ? Number(e.target.value)
                    : undefined,
                })
              }
            >
              <option value="">{t("Select…")}</option>
              {filteredProducts.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          )
        },
        size: 200,
      },
      {
        id: "volumeNet",
        header: () => <span>{t("Total (kg)")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              step="any"
              placeholder={t("0")}
              value={r.volumeNet ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  volumeNet: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 130,
      },
      {
        id: "shareFrance",
        header: () => <span>{t("Products for the French market (%)")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              max={100}
              step="any"
              placeholder={t("0")}
              value={r.totalPercentageOfRecycledPlasticsInFrance ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  totalPercentageOfRecycledPlasticsInFrance: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 160,
      },
      {
        id: "shareInsideEu",
        header: () => (
          <span>
            {t("Products exported outside EU/UK/Norway/Switzerland (%)")}
          </span>
        ),
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              max={100}
              step="any"
              placeholder={t("0")}
              value={r.estimatedShareExportedInsideEu ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  estimatedShareExportedInsideEu: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 200,
      },
      {
        id: "shareOutsideEu",
        header: () => (
          <span>{t("Products exported to Norway and Switzerland (%)")}</span>
        ),
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="number"
              min={0}
              max={100}
              step="any"
              placeholder={t("0")}
              value={r.estimatedShareExportedOutsideEu ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  estimatedShareExportedOutsideEu: e.target.value
                    ? parseFloat(e.target.value)
                    : undefined,
                })
              }
            />
          )
        },
        size: 200,
      },
      {
        id: "extra",
        header: () => <span>{t("Extra information")}</span>,
        cell: ({ row }) => {
          const r = row.original.row
          return (
            <input
              type="text"
              placeholder={t("Free text")}
              value={r.administrationNumber ?? ""}
              onChange={(e) =>
                updateRow(row.original.key, {
                  administrationNumber: e.target.value,
                })
              }
            />
          )
        },
        size: 180,
      },
    ],
    [plants, polymersOf, allProducts, t, updateRow],
  )

  return (
    <SubmissionGrid<ConvertingOutputRow>
      rows={rows}
      columns={columns}
      states={states}
      onAdd={handleAdd}
      onRemove={handleRemove}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    />
  )
}

export default ConvertingOutputGrid
